import { Box } from '@mui/material'
import type { SxProps, Theme } from '@mui/material/styles'
import { useEffect, useMemo, useState } from 'react'
import { IconProjectPlaceholder } from '../../icons/IconProjectPlaceholder'

function normalizeImageSrc(src?: string | null): string | null {
  if (src == null) return null
  const trimmed = src.trim()
  return trimmed.length > 0 ? trimmed : null
}

/** Размер иконки заглушки (геометрия задаётся только через sx снаружи). */
const FALLBACK_PLACEHOLDER_ICON_SIZE = 48

type Props = {
  src?: string | null
  alt?: string
  sx?: SxProps<Theme>
  onClick?: () => void
}

/** Базовые стили первыми, переданный sx последним — приоритет у CardImg / CardMediaStyle / BoxImg. */
const mergeSx = (base: SxProps<Theme>, extra?: SxProps<Theme>): SxProps<Theme> => {
  if (extra == null) return base
  const rest = Array.isArray(extra) ? [...extra] : [extra]
  return [base, ...rest] as SxProps<Theme>
}

export const ProjectCoverImage = ({ src, alt = 'Проект', sx, onClick }: Props) => {
  const normalizedSrc = useMemo(() => normalizeImageSrc(src), [src])
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    setFailed(false)
  }, [normalizedSrc])

  const showFallback = !normalizedSrc || failed
  const clickableSx: SxProps<Theme> | undefined =
    onClick && !showFallback ? { cursor: 'pointer' } : undefined

  const imgBaseSx: SxProps<Theme> = {
    display: 'block',
    objectFit: 'cover',
  }

  const fallbackBaseSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    bgcolor: '#f0f8ff',
    color: '#41bbf0',
  }

  if (showFallback) {
    return (
      <Box sx={mergeSx(fallbackBaseSx, sx)}>
        <IconProjectPlaceholder sx={{ fontSize: FALLBACK_PLACEHOLDER_ICON_SIZE }} />
      </Box>
    )
  }

  return (
    <Box
      component="img"
      src={normalizedSrc}
      alt={alt}
      onClick={onClick}
      onError={() => setFailed(true)}
      sx={mergeSx(mergeSx(imgBaseSx, clickableSx), sx)}
    />
  )
}
